import { Field, Input } from '@zendeskgarden/react-forms';
import type { InputSearchProps, SearchItem } from './interfaces';
import { useUserSearch } from './useInputSearch';

export function InputSearch(props: InputSearchProps) {
  const {
    query,
    results,
    open,
    loading,
    error,
    highlight,
    handleChange,
    handleKeyDown,
    handleBlur,
    handleSelect,
  } = useUserSearch(props);

  return (
    <div className={props.className} style={{ position: 'relative' }}>
      {props.label && <Field.Label>{props.label}</Field.Label>}

      <Input
        type="text"
        value={query}
        disabled={props.disabled}
        placeholder={
          props.placeholder ??
          (props.mode === 'product'
            ? 'Buscar produto (código exato ou texto)'
            : 'Buscar usuário (nome, email, telefone)')
        }
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="search-listbox"
      />

      {open && (
        <div
          role="listbox"
          id="search-listbox"
          className="
            absolute top-[calc(100%+4px)] left-0 right-0 z-10
            bg-white border border-gray-300 rounded-lg
            max-h-72 overflow-y-auto shadow-lg
          "
        >
          {loading && <div className="p-2.5 text-sm">Carregando…</div>}
          {!loading && error && <div className="p-2.5 text-sm text-red-600">{error}</div>}
          {!loading && !error && results.length === 0 && (
            <div className="p-2.5 text-sm">Nenhum resultado</div>
          )}

          {!loading &&
            !error &&
            results.map((item: SearchItem, idx: number) => {
              const isActive = idx === highlight;

              let secondary: string;
              if (item.kind === 'product') {
                secondary = item.custom_object_fields?.codigo_do_produto ?? '—';
              } else if (item.kind === 'nota-fiscal'){
                secondary = item.nameTmp ?? '—';
              } else {
                const email = item.email ?? '—';
                const phone = item.phone ?? '—';
                secondary = `${email} • ${phone}`;
              }

              return (
                <div
                  key={`${item.kind}-${item.id}`}
                  role="option"
                  aria-selected={isActive}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(item);
                  }}
                  className={`px-3 py-2 cursor-pointer border-b border-gray-100 ${
                    isActive ? 'bg-indigo-100' : 'bg-white'
                  }`}
                >
                  <div className="text-sm font-semibold">{item.name}</div>

                  <div className="text-xs text-gray-500">{secondary}</div>

                  {item.kind === 'user' && (
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {[
                        item.cpf && `CPF: ${item.cpf}`,
                        item.cnpj && `CNPJ: ${item.cnpj}`,
                        item.cidade && item.estado && `${item.cidade}/${item.estado}`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
